import { assetUrl, squareSet } from "@/lib/api";
import { AnimatePresence, motion } from "framer-motion";
import { ServePrices } from "@/components/z6/Price";
import { dishTheme } from "@/lib/menuTheme";
import { kcalLabel } from "@/lib/constants";
import { Badge, CourseChip, PatternPanel, imprintKey } from "@/components/z6/Pattern";
import { AllergenRow } from "@/components/z6/AllergenIcon";
import { SectionIcons } from "@/components/z6/IconKey";

const Dot = () => <span aria-hidden className="h-1 w-1 shrink-0 rounded-full bg-[#0B2E44]/30" />;

// Small pattern tile used for thumbnails (summary, Bio-Q options). No food photos.
export const DishPhoto = ({ dish, testId }) => (
  <PatternPanel course={dish.course} testId={testId} className="h-full w-full rounded-2xl">
    <span className="absolute bottom-1 right-1 scale-75"><Badge dish={dish} className="h-8 w-8" /></span>
  </PatternPanel>
);

export const InfoRow = ({ dish, idp }) => (
  <div className="font-ui flex flex-wrap items-center justify-between gap-x-3 gap-y-2 text-[15px] text-[#33566B]">
    <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
      {dish.kcal != null && <span data-testid={`${idp}-kcal-${dish.id}`} className="font-mono text-[15px]">{kcalLabel(dish.kcal)}</span>}
      {dish.kcal != null && dish.allergens.length > 0 && <Dot />}
      <AllergenRow allergens={dish.allergens} testId={`${idp}-allergens-${dish.id}`} />
    </span>
    <span className="ml-auto"><ServePrices dish={dish} testId={`${idp}-price-${dish.id}`} /></span>
  </div>
);

export const DishCard = ({ dish, index = 0, onOpen, badge, active }) => {
  const t = dishTheme(dish);
  return (
    <motion.div layout initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.94, transition: { duration: 0.2 } }}
      transition={{ delay: Math.min(index, 8) * 0.06, type: "spring", stiffness: 120, damping: 18 }}>
      <motion.button type="button" layoutId={`dish-${dish.id}`} data-testid={`dish-card-${dish.id}`} onClick={() => onOpen(dish)} whileTap={{ scale: 0.98 }}
        className="relative flex h-full w-full flex-col overflow-hidden rounded-[22px] bg-white/90 text-left shadow-[0_24px_50px_-30px_rgba(14,59,87,0.45)] ring-1 ring-[#0B2E44]/[0.06] focus:outline-none focus-visible:ring-1 focus-visible:ring-[#0F8A96]">
        <div className="relative">
          {dish.square_photo
            ? <img src={assetUrl(dish.square_photo)} srcSet={squareSet(dish.square_photo)} sizes="(min-width: 1024px) 24vw, (min-width: 640px) 46vw, 92vw" width={1200} height={1200} alt={dish.name} loading="lazy" decoding="async" data-testid={`dish-card-photo-${dish.id}`} className="aspect-square w-full object-cover" />
            : <PatternPanel course={dish.course} imprint={imprintKey(dish, active)} seed={dish.id} testId={`dish-card-gradient-${dish.id}`} className="aspect-square w-full" />}
          {badge && <span data-testid={`dish-card-menu-badge-${dish.id}`} className="absolute left-4 top-4 rounded-full bg-white/85 px-3 py-1 font-mono text-[15px] uppercase tracking-[0.2em] backdrop-blur-md" style={{ color: "#B8326C" }}>{badge}</span>}
          <CourseChip course={dish.course} testId={`dish-card-course-${dish.id}`} className="absolute right-4 top-4" />
          <Badge dish={dish} testId={`dish-card-diet-${dish.id}`} className="absolute bottom-0 left-5 translate-y-1/2" />
          {dish.course !== "beverage" && <span className="absolute bottom-3 right-3"><SectionIcons ids={dish.menus} testId={`dish-card-sections-${dish.id}`} /></span>}
        </div>
        <div className="flex flex-1 flex-col gap-2 px-5 pb-5 pt-9">
          <h3 data-testid={`dish-card-name-${dish.id}`} className="font-title text-[21px] uppercase leading-[1.1] tracking-[0.04em] text-[#0B2E44]">{dish.name}</h3>
          {dish.description && <p className="t-oneliner line-clamp-1">{dish.description}</p>}
          {dish.suitability_note && <p data-testid={`dish-card-hold-${dish.id}`} className="font-ui text-[14px] text-[#B8326C]">{dish.suitability_note}</p>}
          <div className="mt-auto pt-1"><InfoRow dish={dish} idp="dish-card" /></div>
        </div>
      </motion.button>
    </motion.div>
  );
};

// Horizontal drink card for a menu's "Drinks for this menu" section.
export const DrinkCard = ({ dish, onOpen, tagged = true }) => (
  <motion.button type="button" data-testid={tagged ? `menu-drink-card-${dish.id}` : undefined} onClick={() => onOpen(dish)} whileTap={{ scale: 0.98 }}
    className="flex min-h-[112px] w-full items-stretch overflow-hidden rounded-[20px] bg-white/90 text-left shadow-[0_18px_40px_-28px_rgba(14,59,87,0.45)] ring-1 ring-[#0B2E44]/[0.06]">
    <PatternPanel course={dish.course} imprint="beverage" seed={dish.id} className="relative w-28 shrink-0">
      <CourseChip course="beverage" className="absolute left-2 top-2 px-2 text-[15px]" />
      <Badge dish={dish} className="absolute bottom-2.5 left-2.5 h-9 w-9" />
    </PatternPanel>
    <div className="flex min-w-0 flex-1 flex-col gap-1.5 py-4 pl-8 pr-5">
      <h3 className="font-title text-[21px] uppercase leading-tight tracking-[0.04em] text-[#0B2E44]">{dish.name}</h3>
      {dish.description && <p className="t-oneliner line-clamp-1">{dish.description}</p>}
      <InfoRow dish={dish} idp="menu-drink" />
    </div>
  </motion.button>
);

export const DishGrid = ({ dishes, onOpen, badgeFor, testId, active }) => (
  <motion.div layout data-testid={testId} className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7">
    <AnimatePresence mode="popLayout">
      {dishes.map((d, i) => <DishCard key={d.id} dish={d} index={i} onOpen={onOpen} badge={badgeFor?.(d)} active={active} />)}
    </AnimatePresence>
  </motion.div>
);
